import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'

interface StockPoint { date: string; close: number }
interface Company { ticker: string; name: string; weight: number; color: string }
interface IndexData {
  companies: Company[]
  index_data: StockPoint[]
  company_data?: StockPoint[]
  selected_company?: string
  last_updated: string
}

function drawChart(
  canvas: HTMLCanvasElement,
  data: StockPoint[],
  color: string,
  label: string,
  isIndex: boolean,
) {
  const ctx = canvas.getContext('2d')
  if (!ctx || data.length < 2) return

  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  canvas.width = w * dpr
  canvas.height = h * dpr
  ctx.scale(dpr, dpr)

  const pad = { top: 30, right: 20, bottom: 40, left: 60 }
  const cw = w - pad.left - pad.right
  const ch = h - pad.top - pad.bottom

  const prices = data.map(d => d.close)
  const minP = Math.min(...prices) * 0.98
  const maxP = Math.max(...prices) * 1.02
  const range = maxP - minP || 1

  // Background
  ctx.fillStyle = '#0f172a'
  ctx.fillRect(0, 0, w, h)

  // Grid lines
  ctx.strokeStyle = '#1e293b'
  ctx.lineWidth = 1
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + (ch * i) / 4
    ctx.beginPath()
    ctx.moveTo(pad.left, y)
    ctx.lineTo(w - pad.right, y)
    ctx.stroke()
    // Price label
    const price = maxP - (range * i) / 4
    ctx.fillStyle = '#64748b'
    ctx.font = '11px system-ui'
    ctx.textAlign = 'right'
    ctx.fillText(isIndex ? price.toFixed(1) : '$' + price.toFixed(2), pad.left - 8, y + 4)
  }

  // Date labels
  const step = Math.max(1, Math.floor(data.length / 6))
  ctx.fillStyle = '#64748b'
  ctx.font = '10px system-ui'
  ctx.textAlign = 'center'
  for (let i = 0; i < data.length; i += step) {
    const x = pad.left + (cw * i) / (data.length - 1)
    const d = data[i].date
    ctx.fillText(d.slice(5), x, h - 10) // MM-DD
  }

  // Line
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.beginPath()
  for (let i = 0; i < data.length; i++) {
    const x = pad.left + (cw * i) / (data.length - 1)
    const y = pad.top + ch - ((data[i].close - minP) / range) * ch
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.stroke()

  // Fill under line
  const lastX = pad.left + cw
  ctx.lineTo(lastX, pad.top + ch)
  ctx.lineTo(pad.left, pad.top + ch)
  ctx.closePath()
  const grad = ctx.createLinearGradient(0, pad.top, 0, pad.top + ch)
  grad.addColorStop(0, color + '40')
  grad.addColorStop(1, color + '05')
  ctx.fillStyle = grad
  ctx.fill()

  // Label
  ctx.fillStyle = '#f8fafc'
  ctx.font = 'bold 14px system-ui'
  ctx.textAlign = 'left'
  ctx.fillText(label, pad.left + 10, pad.top + 20)

  // Current price
  const last = data[data.length - 1]
  const first = data[0]
  const changePct = ((last.close - first.close) / first.close * 100)
  const changeStr = (changePct >= 0 ? '+' : '') + changePct.toFixed(1) + '%'
  ctx.fillStyle = changePct >= 0 ? '#22c55e' : '#ef4444'
  ctx.font = 'bold 13px system-ui'
  ctx.textAlign = 'right'
  ctx.fillText(
    (isIndex ? last.close.toFixed(1) : '$' + last.close.toFixed(2)) + '  ' + changeStr,
    w - pad.right - 10,
    pad.top + 20,
  )
}

function QuantumIndexPage() {
  const [data, setData] = useState<IndexData | null>(null)
  const [period, setPeriod] = useState('6mo')
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const indexCanvasRef = useRef<HTMLCanvasElement>(null)
  const companyCanvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    setIsLoading(true)
    const url = `/api/quantum-index/data?period=${period}` + (selectedCompany ? `&company=${selectedCompany}` : '')
    fetch(url).then(r => r.json()).then(d => {
      setData(d)
      setIsLoading(false)
    }).catch(() => setIsLoading(false))
  }, [period, selectedCompany])

  // Draw index chart
  useEffect(() => {
    if (!data?.index_data?.length || !indexCanvasRef.current) return
    drawChart(indexCanvasRef.current, data.index_data, '#8b5cf6', 'Quantum Hype Index', true)
  }, [data?.index_data])

  // Draw company chart
  useEffect(() => {
    if (!data?.company_data?.length || !companyCanvasRef.current || !selectedCompany) return
    const company = data.companies.find(c => c.ticker === selectedCompany)
    drawChart(
      companyCanvasRef.current,
      data.company_data,
      company?.color || '#3b82f6',
      company?.name || selectedCompany,
      false,
    )
  }, [data?.company_data, selectedCompany])

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="bg-slate-900/80 backdrop-blur border-b border-slate-800 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/"><Logo size="small" /></Link>
          <Link to="/" className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            QuantumArena
          </Link>
          <span className="text-slate-600">/</span>
          <span className="text-slate-300 font-medium">Quantum Hype Index</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">Home</Link>
          <Link to="/arena" className="text-sm text-slate-400 hover:text-white transition-colors">Arena</Link>
          <AuthHeader />
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">
            Quantum Hype Index
          </h1>
          <p className="text-slate-400 mt-2">
            Weighted index of quantum computing company stock prices. Track the hype in real time.
          </p>
        </div>

        {/* Period selector */}
        <div className="flex gap-2 mb-6">
          {['1mo', '3mo', '6mo', '1y', '2y'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Index chart */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 mb-6">
              <canvas ref={indexCanvasRef} className="w-full" style={{ height: 320 }} />
            </div>

            {/* Company weights */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {data?.companies.map(c => (
                <button
                  key={c.ticker}
                  onClick={() => setSelectedCompany(selectedCompany === c.ticker ? null : c.ticker)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    selectedCompany === c.ticker
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-slate-800 bg-slate-900 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                    <span className="font-bold text-sm text-white">{c.ticker}</span>
                  </div>
                  <div className="text-xs text-slate-400">{c.name}</div>
                  <div className="text-[10px] text-slate-500 mt-1">Weight: {(c.weight * 100).toFixed(0)}%</div>
                </button>
              ))}
            </div>

            {/* Company chart */}
            {selectedCompany && data?.company_data && data.company_data.length > 0 && (
              <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 mb-6">
                <canvas ref={companyCanvasRef} className="w-full" style={{ height: 280 }} />
              </div>
            )}

            {/* Index explanation */}
            <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-6">
              <h3 className="font-semibold text-slate-200 mb-3">How the Index Works</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                The Quantum Hype Index normalizes each company's stock to a base of 100 at the start of the selected period,
                then computes a weighted average. This shows the collective trend of the quantum computing sector
                regardless of individual stock price magnitudes. A rising index means quantum stocks are collectively
                outperforming their period start — a measure of "quantum hype" in the market.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default QuantumIndexPage
